import {
  parseISO,
  isValid,
  isEqual,
  closestTo,
  differenceInCalendarDays,
  startOfDay,
  startOfISOWeek,
  addHours,
  addDays,
  addWeeks,
  addMonths,
  addYears,
  subDays,
  subHours,
  subWeeks,
  subMonths,
  subYears,
} from "date-fns/esm"

import { globalI18n as i18n } from "../i18n/withI18n"
import { defaultLanguage } from "../../config"

export {
  parseISO,
  isValid,
  isEqual,
  closestTo,
  differenceInCalendarDays,
  startOfDay,
  startOfISOWeek,
  addHours,
  addDays,
  addWeeks,
  addMonths,
  addYears,
  subHours,
  subDays,
  subWeeks,
  subMonths,
  subYears,
}

const DATE_TIME_FORMAT_DEFAULTS = {
  year: "numeric",
  month: "short",
  day: "numeric",
}

const RELATIVE_TIME_FORMAT_DEFAULTS = { numeric: "auto" }

const getLocale = () => i18n?.language || defaultLanguage

const throwInvalidDateError = field => {
  throw new Error(`${field} value should be instance of Date`)
}

/**
 * @function format
 * Return the formatted date string according to chosen locale.
 * @param {Date} date - The date object to be formatted
 * @param {Object} options - The options passed to Intl.DateTimeFormat constructor
 * @param {string|string[]} [locale] - Optional override for default locale(s)
 * @returns {string}
 */
export const format = (
  date,
  options = DATE_TIME_FORMAT_DEFAULTS,
  locale = getLocale()
) => {
  if (!(date instanceof Date)) throwInvalidDateError("date")

  return new Intl.DateTimeFormat(locale, options).format(date)
}

const getIntlSupport = () => typeof Intl.RelativeTimeFormat === "function"

/**
 * @function formatDistance
 * Return the distance between the given dates in words according to chosen locale.
 * @param {Date} date - The date object
 * @param {Date} baseDate - The date object to compare with
 * @param {Object} options - The options passed to Intl.RelativeTimeFormat constructor
 * @param {string|string[]} [locale] - Optional override for default locale(s)
 * @returns {string}
 */
export const formatDistance = (
  date,
  baseDate,
  options = RELATIVE_TIME_FORMAT_DEFAULTS,
  locale = getLocale()
) => {
  if (!(date instanceof Date)) throwInvalidDateError("date")
  if (!(baseDate instanceof Date)) throwInvalidDateError("baseDate")

  if (!getIntlSupport()) return format(date)

  const diff = date - baseDate

  const msPerSecond = 1000
  const msPerMinute = msPerSecond * 60
  const msPerHour = msPerMinute * 60
  const msPerDay = msPerHour * 24
  const msPerMonth = msPerDay * 30
  const msPerYear = msPerDay * 365

  const intl = new Intl.RelativeTimeFormat(locale, options)

  if (Math.abs(diff) < msPerMinute)
    return intl.format(Math.round(diff / msPerSecond), "second")
  if (Math.abs(diff) < msPerHour)
    return intl.format(Math.round(diff / msPerMinute), "minute")
  if (Math.abs(diff) < msPerDay)
    return intl.format(Math.round(diff / msPerHour), "hour")
  if (Math.abs(diff) < msPerMonth)
    return intl.format(Math.round(diff / msPerDay), "day")
  if (Math.abs(diff) < msPerYear)
    return intl.format(Math.round(diff / msPerMonth), "month")

  return intl.format(Math.round(diff / msPerYear), "year")
}
